import { useEffect, useRef, type ReactNode, type RefObject } from 'react';

export interface InfiniteScrollSentinelProps {
  /** Called when the sentinel comes within the prefetch distance of the scroll area's end. */
  onVisible: () => void;
  /** No more pages, a request in flight or a failure: stop observing. */
  disabled?: boolean;
  /** Scroll container; omitted = the viewport. */
  rootRef?: RefObject<Element | null>;
  /**
   * How early to ask, as a fraction of the scroll area's height before the end (default 0.3: the next page is
   * requested when ~70 % of the loaded rows have been scrolled).
   */
  prefetch?: number;
  /** Changes whenever rows were added, so a sentinel that is still in view asks again. */
  watch?: unknown;
  /** Optional footer (spinner, "load more" button as keyboard/no-IntersectionObserver fallback). */
  children?: ReactNode;
}

/**
 * Place after the last row of a list driven by `useInfiniteList`:
 * `<InfiniteScrollSentinel onVisible={list.loadMore} disabled={!list.hasMore || list.loadingMore || Boolean(list.error)} watch={list.items.length} />`.
 * Without IntersectionObserver (old browsers, jsdom) it renders only its children.
 */
export function InfiniteScrollSentinel({ onVisible, disabled = false, rootRef, prefetch = 0.3, watch, children }: InfiniteScrollSentinelProps) {
  const target = useRef<HTMLDivElement>(null);
  const callback = useRef(onVisible);
  useEffect(() => {
    callback.current = onVisible;
  });
  useEffect(() => {
    const element = target.current;
    if (disabled || !element || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) callback.current();
      },
      { root: rootRef?.current ?? null, rootMargin: `0px 0px ${Math.round(prefetch * 100)}% 0px` },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [disabled, rootRef, prefetch, watch]);
  return (
    <div ref={target} data-slot="infinite-scroll-sentinel">
      {children}
    </div>
  );
}
